"use client"

import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { useTranslations } from "next-intl"
import { createClient } from "@/utils/supabase/client"
import { v4 as uuidv4 } from "uuid"
import Image from "next/image"
import { motion } from "framer-motion"
import { Check, Pencil, AlertTriangle } from "lucide-react"
import { getNormalizedImageFile, getSafeImageExtension, cleanupOrphanedUploads, uploadWithConcurrency } from "@/lib/image-upload"
import { storageBucket, StoragePath } from "@/lib/storage"
import { formatPrice } from "@/lib/utils"
import type { StepProps, PhotoItem, CardListingDraft } from "@/types/listing"
import {
  creationFlowBlockClass,
  creationFlowSectionLabelClass,
  creationFlowPrimaryCtaTallClass,
} from "@/lib/creation-flow-form-styles"
import { cn } from "@/lib/utils"
import { useToast } from "@/hooks/use-toast"
import { normalizeShippingConfig } from "@/lib/shipping-config"
import {
  createListing,
  CreateListingError,
  type CreateListingIntent,
} from "@/lib/listings/listing-create"
import { asRawImageRef, rawImageRefValue, resolveStorageUrl, type RawImageRef } from "@/lib/storage/public-url"
import { isPersistedImageRef } from "@/lib/storage/url"
import { CARD_SM, imagePresetUrl } from "@/lib/images/presets"

// Canonical predicate — a bucket-relative storage path counts as persisted
// too, otherwise every freshly-uploaded image is filtered out once writers
// stop calling getPublicUrl(). @see lib/storage/url.ts
const isHttpUrl = isPersistedImageRef

interface ValidationError {
  field: string
  message: string
}

function validateCardDraft(data: StepProps["data"]): ValidationError[] {
  const errors: ValidationError[] = []
  const draft = data.cardDraft
  const photos = Array.isArray(data.editedPhotos) && data.editedPhotos.length > 0 ? data.editedPhotos : data.photos

  if (!photos || photos.length === 0) {
    errors.push({ field: "photos", message: "At least 1 photo is required" })
  }
  if (!draft) {
    errors.push({ field: "cardDraft", message: "Card data is missing" })
    return errors
  }
  if (!draft.cardName?.trim()) {
    errors.push({ field: "cardName", message: "Card name is required" })
  }
  if (!draft.format) {
    errors.push({ field: "format", message: "Listing format is required" })
  }
  if (!draft.tcgId) {
    errors.push({ field: "tcgId", message: "TCG / Game is required" })
  }
  const listingFormat = data.listing_format ?? "fixed_price"
  const isOffersOnly = listingFormat === "offers_only"
  const isAuction = listingFormat === "auction"
  if (!isOffersOnly && !isAuction && (!data.price || data.price <= 0)) {
    errors.push({ field: "price", message: "Price must be greater than 0" })
  }
  if (isAuction && (!data.auction_start_price || data.auction_start_price <= 0)) {
    errors.push({ field: "auction_start_price", message: "Auction start price is required" })
  }
  if (!isAuction && (!data.quantity || data.quantity < 1)) {
    errors.push({ field: "quantity", message: "Quantity must be at least 1" })
  }

  const isSealed = draft.format && ["booster", "box", "etb", "deck"].includes(draft.format)
  if (!isSealed) {
    if (draft.isGraded) {
      if (!draft.grading?.companyId) {
        errors.push({ field: "grading.companyId", message: "Grading company is required" })
      }
      if (draft.grading?.bgsSubgrades) {
        const { centering, corners, edges, surface } = draft.grading.bgsSubgrades
        const filled = [centering, corners, edges, surface].filter((v) => v != null)
        if (filled.length > 0 && filled.length < 4) {
          errors.push({ field: "bgsSubgrades", message: "All 4 BGS sub-scores must be provided" })
        }
      }
    } else {
      if ((draft.format === "single" || draft.format === "slab") && !draft.conditionId) {
        errors.push({ field: "conditionId", message: "Condition is required for raw cards" })
      }
    }
  }

  return errors
}

export function CardReviewStep({
  data,
  updateDataAction,
  onSubmit,
  hideButton,
  onValidationChange,
  onRegisterSave,
  onGoToStepAction,
  compact,
}: StepProps) {
  const t = useTranslations("cardCreation.review")
  const [isCreating, setIsCreating] = useState(false)
  const { toast } = useToast()
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])
  const [taxonomyNames, setTaxonomyNames] = useState<Record<string, string>>({})

  const draft = data.cardDraft

  const errors = useMemo(() => validateCardDraft(data), [data])

  useEffect(() => {
    setValidationErrors(errors)
    onValidationChange?.(errors.length === 0)
  }, [errors, onValidationChange])

  useEffect(() => {
    if (!draft) return
    const ids = [draft.tcgId, draft.tcgSetId, draft.tcgRarityId, draft.languageId, draft.conditionId].filter(Boolean) as string[]
    if (ids.length === 0) return

    const supabase = createClient()
    supabase
      .from("taxonomy_items")
      .select("id, name")
      .in("id", ids)
      .then(({ data: rows }) => {
        if (!rows) return
        const map: Record<string, string> = {}
        rows.forEach((r: { id: string; name: string }) => { map[r.id] = r.name })
        setTaxonomyNames(map)
      })
  }, [draft?.tcgId, draft?.tcgSetId, draft?.tcgRarityId, draft?.languageId, draft?.conditionId])

  const handleCreateListing = useCallback(async () => {
    if (errors.length > 0 || !draft) return
    setIsCreating(true)

    const supabase = createClient()
    const sourcePhotos = Array.isArray(data.editedPhotos) && data.editedPhotos.length > 0 ? data.editedPhotos : data.photos
    const photosCopy = [...sourcePhotos]
    const uploadedUrls: string[] = []

    // `RawImageRef | null` — every branch returns a value destined for
    // `listings.image_urls`, so nothing in this chain may resolve (handoff §9).
    const uploadTasks = photosCopy.map((photo, i) => async (): Promise<RawImageRef | null> => {
      if (photo?.file instanceof File) {
        const MAX_SIZE = 10 * 1024 * 1024
        if (photo.file.size > MAX_SIZE) return null
        try {
          let fileToUpload = photo.file
          try {
            const normalized = await getNormalizedImageFile(photo.file)
            fileToUpload = normalized.file
          } catch { /* use original */ }
          const ext = getSafeImageExtension(fileToUpload)
          const path = StoragePath.listing(data.title || "card", i, ext)
          const { error } = await storageBucket(supabase).upload(path, fileToUpload)
          if (error) return null
          return asRawImageRef(path)
        } catch { return null }
      } else if (photo?.preview && isHttpUrl(photo.preview)) {
        return photo.preview
      } else if (rawImageRefValue(photo?.preview)?.startsWith("blob:")) {
        try {
          const resp = await fetch(resolveStorageUrl(photo.preview))
          if (!resp.ok) return null
          const blob = await resp.blob()
          const file = new File([blob], `blob-${Date.now()}-${i}.jpg`, { type: blob.type || "image/jpeg" })
          const ext = getSafeImageExtension(file)
          const path = StoragePath.listing(data.title || "card", i, ext)
          const { error } = await storageBucket(supabase).upload(path, file)
          if (error) return null
          return asRawImageRef(path)
        } catch { return null }
      }
      return null
    })

    const results = await uploadWithConcurrency(uploadTasks, 3)
    const validUrls = results.filter((url): url is RawImageRef => url !== null && isHttpUrl(url))

    if (validUrls.length === 0 && photosCopy.length > 0) {
      toast({ title: "Error", description: t("errorUpload"), variant: "destructive" })
      setIsCreating(false)
      return
    }

    try {
      const listingId = uuidv4()
      // Phase 4 (2026-06-29) of shippo-listing-discriminator: visibility enum replaces is_chat_private
      const isChatPrivate = (data as unknown as Record<string, unknown>).visibility === "chat_private"
      const chatPrivateSaved = Boolean((data as unknown as Record<string, unknown>).chat_private_saved)

      const isAuction = (data as { listing_format?: string }).listing_format === "auction"
      const isOffersOnly = (data as { listing_format?: string }).listing_format === "offers_only"

      const shippingMode = data.shippingMode ?? "legacy"
      const isLiveRate = shippingMode === "live_rate"
      const parcel = data.parcel
      const payload: Record<string, unknown> = {
        id: listingId,
        title: draft.cardName,
        description: data.description || "",
        price: isAuction || isOffersOnly ? null : data.price,
        quantity: isAuction ? 1 : (data.quantity ?? 1),
        shipping_config: normalizeShippingConfig(data.shipping),
        // listings.location dropped — ADR_LISTING_SHIP_FROM_ADDRESS Phase 3.
        image_urls: validUrls,
        condition: "",
        grade: "",
        scheduled_publish_at: isChatPrivate ? null : (data.scheduled_publish_at || null),
        is_dev_listing: isChatPrivate ? false : (data.is_dev_listing ?? false),
        visibility: isChatPrivate ? "chat_private" : "public",
        chat_private_saved: isChatPrivate ? chatPrivateSaved : false,
        cardCategory: "trading_card",
        cardDraft: draft,
        shipping_mode: shippingMode,
        carrier_provider: isLiveRate ? "shippo" : null,
        // Canonical-only (slice C2): the form emits grams + mm; we forward as-is.
        parcel_weight_grams: isLiveRate ? parcel?.weight ?? null : null,
        parcel_length_mm: isLiveRate ? parcel?.length ?? null : null,
        parcel_width_mm: isLiveRate ? parcel?.width ?? null : null,
        parcel_height_mm: isLiveRate ? parcel?.height ?? null : null,
      }

      const fmt = (data as { listing_format?: string }).listing_format
      if (isAuction) {
        payload.listing_format = "auction"
        payload.auction_start_price = Number((data as { auction_start_price?: number }).auction_start_price ?? 0)
        payload.auction_reserve_price = (data as { auction_reserve_price?: number | null }).auction_reserve_price ?? null
      } else if (fmt === "offers_only" || fmt === "fixed_price_with_offers" || fmt === "fixed_price") {
        // Phase 3 (2026-06-29): `accepts_offers` collapsed into
        // `listing_format`. DetailsStep emits the final format
        // (`fixed_price_with_offers` when the toggle is on) — pass it through.
        payload.listing_format = fmt
        payload.auto_decline_below = (data as { auto_decline_below?: number | null }).auto_decline_below ?? null
      }

      if ((data as { listing_type?: string }).listing_type === "preorder") {
        payload.listing_type = "preorder"
        payload.preorder_deadline = (data as { preorder_deadline?: string | null }).preorder_deadline ?? null
        payload.estimated_delivery = (data as { estimated_delivery?: string | null }).estimated_delivery ?? null
        payload.min_orders = (data as { min_orders?: number | null }).min_orders ?? null
        payload.max_orders = (data as { max_orders?: number | null }).max_orders ?? null
      }

      let result: { listingId: string }
      try {
        result = await createListing(payload as CreateListingIntent)
      } catch (apiErr) {
        if (apiErr instanceof CreateListingError) {
          const message = apiErr.serverError ?? t("errorGeneric")
          toast({ title: "Error", description: message, variant: "destructive" })
          await cleanupOrphanedUploads(validUrls)
          return
        }
        throw apiErr
      }

      const resolvedId = result.listingId || listingId
      const finalPhotoItems = validUrls.map((url, i) => ({
        file: null,
        preview: url,
        photoId: `uploaded-${i}-${Date.now()}`,
      }))
      updateDataAction({
        photos: finalPhotoItems,
        editedPhotos: finalPhotoItems,
        photoEditorState: undefined,
        id: resolvedId,
      })
      if (onSubmit) onSubmit()
    } catch (err) {
      console.error("[card-review] create failed", err)
      toast({ title: "Error", description: t("errorCreate"), variant: "destructive" })
      await cleanupOrphanedUploads(validUrls)
    } finally {
      setIsCreating(false)
    }
  }, [data, draft, errors, toast, updateDataAction, onSubmit])

  useEffect(() => {
    if (onRegisterSave) {
      onRegisterSave(() => {
        if (!isCreating) return handleCreateListing()
      })
      return () => onRegisterSave(null)
    }
  }, [onRegisterSave, isCreating, handleCreateListing])

  const editBtn = (step: number) =>
    onGoToStepAction ? (
      <button
        type="button"
        onClick={() => onGoToStepAction(step)}
        className="ml-auto flex items-center gap-1 text-xs font-medium rounded-lg px-2 py-1 transition-colors text-muted-foreground hover:text-foreground hover:bg-foreground/10"
      >
        <Pencil className="w-3 h-3" />{t("edit")}
      </button>
    ) : null

  const cardRadius = "rounded-3xl"
  const cardBase = creationFlowBlockClass
  const cardPadding = compact ? "p-3" : "p-4"
  const sectionLabel = creationFlowSectionLabelClass
  const cellBase = "rounded-xl bg-muted/80 p-3"
  const mutedClass = "text-muted-foreground/70"
  const textClass = "text-foreground"

  const photos = Array.isArray(data.editedPhotos) && data.editedPhotos.length > 0 ? data.editedPhotos : data.photos

  return (
    <div className={compact ? "space-y-4 px-1" : "space-y-6 px-2"}>
      {validationErrors.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-xl p-3 border bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800/40">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0" />
            <span className="text-sm font-medium text-red-700 dark:text-red-300">{t("missingFields")}</span>
          </div>
          <ul className="space-y-1">
            {validationErrors.map((e) => (
              <li key={e.field} className="text-xs text-red-600 dark:text-red-400">• {e.message}</li>
            ))}
          </ul>
        </motion.div>
      )}

      <motion.div className="space-y-5" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className={cn(cardRadius, cardBase, cardPadding)}>
          <div className="flex items-center mb-3">
            <p className={cn(sectionLabel)}>{t("photos")} ({photos.length})</p>
            {editBtn(1)}
          </div>
          <div className="flex overflow-x-auto gap-2 pb-1 scrollbar-none [&::-webkit-scrollbar]:hidden">
            {photos.map((photo: PhotoItem, i: number) => {
              // RESOLVE at the sink: after a successful create this list is
              // re-seeded with bucket-relative paths (`finalPhotoItems`), and a
              // bare path in `next/image` throws `Failed to construct 'URL'`.
              const src = resolveStorageUrl(photo?.preview) || ""
              if (!src) return null
              return (
                <div key={i} className="relative w-20 shrink-0 overflow-hidden rounded-xl aspect-3/4 bg-muted">
                  {/*
                    Same two-lives source as the non-card `review-step.tsx`:
                    `blob:` before the create, a bucket path after it.
                    `imagePresetUrl` passes the blob through and CDN-sizes the
                    path. `unoptimized` was redundant on the blob frame (Next
                    force-sets it for `blob:`/`data:` itself) and is load-bearing
                    on the stored-path frame, where the URL is already CDN-sized
                    and re-optimising it would cost a hop plus a billed
                    transformation. CARD_SM rather than a square SQUARE_*: the tile
                    is `aspect-3/4` and already crops via `object-cover`, and
                    cropping twice would tighten the framing on a
                    photo-verification screen.
                  */}
                  <Image src={imagePresetUrl(src, CARD_SM) || "/placeholder.svg"} alt={`Photo ${i + 1}`} fill className="object-cover" unoptimized />
                </div>
              )
            })}
          </div>
        </motion.div>

        {draft && (
          <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className={cn(cardRadius, cardBase, cardPadding)}>
            <div className="flex items-center mb-3">
              <p className={cn(sectionLabel)}>{t("cardCategory")}</p>
              {editBtn(3)}
            </div>
            <div className="grid grid-cols-1 gap-2">
              {draft.tcgId && <div className={cellBase}><p className={cn("text-xs", mutedClass)}>{t("tcg")}</p><p className={cn("text-sm font-medium", textClass)}>{taxonomyNames[draft.tcgId] ?? "—"}</p></div>}
              {draft.tcgSetId && <div className={cellBase}><p className={cn("text-xs", mutedClass)}>{t("set")}</p><p className={cn("text-sm font-medium", textClass)}>{taxonomyNames[draft.tcgSetId] ?? "—"}</p></div>}
              {draft.tcgRarityId && <div className={cellBase}><p className={cn("text-xs", mutedClass)}>{t("rarityLabel")}</p><p className={cn("text-sm font-medium", textClass)}>{taxonomyNames[draft.tcgRarityId] ?? "—"}</p></div>}
              <div className={cellBase}><p className={cn("text-xs", mutedClass)}>{t("format")}</p><p className={cn("text-sm font-medium", textClass)}>{draft.format}</p></div>
              {draft.languageId && <div className={cellBase}><p className={cn("text-xs", mutedClass)}>{t("languageLabel")}</p><p className={cn("text-sm font-medium", textClass)}>{taxonomyNames[draft.languageId] ?? "—"}</p></div>}
            </div>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className={cn(cardRadius, cardBase, cardPadding)}>
          <div className="flex items-center mb-3">
            <p className={cn(sectionLabel)}>{t("details")}</p>
            {editBtn(4)}
          </div>
          <div className="space-y-2">
            <p className={cn("font-medium", textClass)}>{draft?.cardName || data.title}</p>
            {draft?.cardNumber && <p className={cn("text-sm", mutedClass)}>#{draft.cardNumber}</p>}
            <p className={cn("text-sm", textClass)}>{t("quantityLabel")}: {data.quantity}</p>
            <p className={cn("text-sm font-medium", textClass)}>
              {t("priceLabel")}: {data.price ? formatPrice(data.price, data.currency || "USD") : t("priceNotSet")}
            </p>
          </div>
        </motion.div>

        {draft && (
          <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className={cn(cardRadius, cardBase, cardPadding)}>
            <div className="flex items-center mb-3">
              <p className={cn(sectionLabel)}>{t("conditionGrading")}</p>
              {editBtn(5)}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {draft.isGraded ? (
                <>
                  <div className={cellBase}><p className={cn("text-xs", mutedClass)}>{t("graded")}</p><p className={cn("text-sm font-medium", textClass)}>{t("yesSlab")}</p></div>
                  {draft.grading?.certNumber && <div className={cellBase}><p className={cn("text-xs", mutedClass)}>{t("certNumber")}</p><p className={cn("text-sm font-medium", textClass)}>{draft.grading.certNumber}</p></div>}
                </>
              ) : (
                <>
                  <div className={cellBase}>
                    <p className={cn("text-xs", mutedClass)}>{t("condition")}</p>
                    <p className={cn("text-sm font-medium", textClass)}>{draft.conditionId ? taxonomyNames[draft.conditionId] ?? "—" : t("na")}</p>
                  </div>
                  {draft.centering && (
                    <div className={cellBase}>
                      <p className={cn("text-xs", mutedClass)}>{t("centeringLabel")}</p>
                      <p className={cn("text-sm font-medium", textClass)}>{draft.centering.leftPct}/{100 - draft.centering.leftPct} · {draft.centering.topPct}/{100 - draft.centering.topPct}</p>
                    </div>
                  )}
                </>
              )}
            </div>
            {draft.surfaceDefects && draft.surfaceDefects.length > 0 && (
              <div className="mt-2">
                <p className={cn("text-xs mb-1", mutedClass)}>{t("defects")}</p>
                <div className="flex flex-wrap gap-1">
                  {draft.surfaceDefects.map((d) => (
                    <span key={d} className="text-xs px-2 py-0.5 rounded bg-muted text-foreground/80">{d.replace("_", " ")}</span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* ADR_LISTING_SHIP_FROM_ADDRESS Phase 3 — Location section removed.
            Origin is selected/displayed inside ShippingStep via
            ShipFromAddressPicker. */}

        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className={cn(cardRadius, cardBase, cardPadding)}>
          <div className="flex items-center mb-3">
            <p className={cn(sectionLabel)}>{t("shippingOptions")}</p>
            {editBtn(7)}
          </div>
          <div className={cellBase}>
            {data.shipping?.methods && data.shipping.methods.length > 0 ? (
              <ul className="space-y-1">
                {data.shipping.methods.map((id: string) => {
                  const custom = data.shipping.customOptions.find((o: { id: string }) => o.id === id)
                  return (
                    <li key={id} className="flex items-center gap-2">
                      <Check className="w-4 h-4 shrink-0 text-primary" />
                      <span className={cn("text-sm", textClass)}>
                        {custom ? `${custom.label} – ${formatPrice(custom.price || 0, data.currency || "USD")}` : id}
                      </span>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className={cn("text-sm", mutedClass)}>{t("noShipping")}</p>
            )}
          </div>
        </motion.div>
      </motion.div>
    </div>
  )
}
